import { settleAuthFirst, shouldClearToken } from './api-client';

export type SessionSnapshot = { token: string | null; household: string | null };
export type StartupOutcome<C, H> =
  | { kind: 'ok'; catalog: C; household: H }
  | { kind: 'auth'; error: unknown }
  | { kind: 'offline'; error: unknown }
  | { kind: 'stale' };

export function sameSession(a: SessionSnapshot, b: SessionSnapshot): boolean {
  return a.token === b.token && a.household === b.household;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isValidCatalog(c: unknown): c is { categories: any[]; tags: any[] } {
  return isObject(c)
    && Array.isArray(c.categories)
    && c.categories.every(category => isObject(category)
      && typeof category.id === 'string'
      && (category.subcategories === undefined
        || (Array.isArray(category.subcategories) && category.subcategories.every(isObject))))
    && Array.isArray(c.tags)
    && c.tags.every(isObject);
}

export function isValidHousehold(h: unknown): h is { name: string; members: any[]; invites: any[] } {
  return isObject(h)
    && typeof h.name === 'string'
    && Array.isArray(h.members) && h.members.every(isObject)
    && Array.isArray(h.invites) && h.invites.every(isObject);
}

export async function loadStartup<C, H>(deps: {
  fetchCatalog: () => Promise<C>;
  fetchHousehold: () => Promise<H>;
  snapshot: () => SessionSnapshot;
  generation: () => number;
}, start: { session: SessionSnapshot; generation: number }): Promise<StartupOutcome<C, H>> {
  let outcome: StartupOutcome<C, H>;
  try {
    const [catalog, household] = await settleAuthFirst([deps.fetchCatalog(), deps.fetchHousehold()]);
    outcome = { kind: 'ok', catalog, household };
  } catch (error) {
    outcome = shouldClearToken(error) ? { kind: 'auth', error } : { kind: 'offline', error };
  }
  if (deps.generation() !== start.generation || !sameSession(start.session, deps.snapshot())) {
    return { kind: 'stale' };
  }
  return outcome;
}
